import { createSlice } from "@reduxjs/toolkit";
import { askMyJournal, fixCsvImport, indexUserData } from "./thunks";
import { me, getUserFromSession } from "../user/thunks";
import { authenticate, confirm2FA, googleSSO, logout, expiredLogout } from "../auth/thunks";
import { confirmPayment } from "../payments/thunks";
import { addAsyncLifecycleCases, addFulfilledCases } from "../utils/asyncHandlers";

const initialState = {
  aiAnswer: {},
  // While the agent is streaming, the partial answer accumulates
  // here (raw token text, before citation humanization). On the
  // `complete` event we replace `aiAnswer` with the full structured
  // payload — including the cleaned answer — and clear this string.
  // The chat UI shows aiAnswerStreaming in the pending entry while
  // it grows, then the resolved entry once aiAnswer lands.
  aiAnswerStreaming: "",
  aiLoading: false,
  aiIndexing: false,
  aiError: false,
  aiJournalQueriesRemaining: null,
  aiCsvQueriesRemaining: null,
};

const applyQueryCounts = (state, action) => {
  const payload = action.payload;
  if (!payload) return;
  if (payload.ai_journal_queries_remaining !== undefined) {
    state.aiJournalQueriesRemaining = payload.ai_journal_queries_remaining;
  }
  if (payload.ai_csv_queries_remaining !== undefined) {
    state.aiCsvQueriesRemaining = payload.ai_csv_queries_remaining;
  }
};

const aiSlice = createSlice({
  name: "ai",
  initialState,
  reducers: {
    // Lifecycle for the SSE streaming path. The thunk in thunks.js
    // dispatches these directly (not via createAsyncThunk lifecycle
    // events) because a streaming request doesn't fit the
    // pending/fulfilled/rejected model — it has many "fulfilled" events.
    chatStreamStarted(state) {
      state.aiAnswerStreaming = "";
      state.aiAnswer = {};
      state.aiLoading = true;
      state.aiError = false;
    },
    chatChunkAppended(state, action) {
      state.aiAnswerStreaming += action.payload?.delta || "";
    },
    chatCompleted(state, action) {
      // Replace partial with the structured payload — this is the
      // humanized answer plus citations / aggregation / filters.
      state.aiAnswer = action.payload || {};
      state.aiAnswerStreaming = "";
      state.aiLoading = false;
      state.aiError = false;
      if (action.payload && action.payload.remaining_queries !== undefined) {
        state.aiJournalQueriesRemaining = action.payload.remaining_queries;
      }
    },
    chatStreamErrored(state, action) {
      state.aiError = true;
      state.aiLoading = false;
      state.aiAnswerStreaming = "";
      const payload = action.payload || {};
      if (payload.quota_exceeded) {
        state.aiAnswer = { answer: payload.result };
        state.aiJournalQueriesRemaining = 0;
      } else {
        // Surface a `result`-bearing error too (e.g. payload-too-large 413,
        // rate-limit 429) — these come back from the HTTP layer with `result`
        // rather than `answer`. TRADE-1010.
        state.aiAnswer =
          payload.answer !== undefined
            ? payload
            : { answer: payload.result || "An error occurred. Please try again." };
      }
    },
  },
  extraReducers: (builder) => {
    addFulfilledCases(
      builder,
      [me, getUserFromSession, authenticate, confirm2FA, googleSSO, confirmPayment],
      applyQueryCounts
    );
    builder
      .addCase(askMyJournal.pending, (state) => {
        state.aiLoading = true;
        state.aiError = false;
      })
      .addCase(askMyJournal.fulfilled, (state, action) => {
        state.aiAnswer = action.payload;
        state.aiLoading = false;
        state.aiError = false;
        if (action.payload && action.payload.remaining_queries !== undefined) {
          state.aiJournalQueriesRemaining = action.payload.remaining_queries;
        }
      })
      .addCase(askMyJournal.rejected, (state, action) => {
        state.aiError = true;
        state.aiLoading = false;
        if (action.payload && action.payload.quota_exceeded) {
          state.aiAnswer = { answer: action.payload.result };
          state.aiJournalQueriesRemaining = 0;
        } else {
          // Prefer a real `answer`, else a `result`-bearing message (spend
          // cap / size limit), else the generic fallback. TRADE-1010.
          const p = action.payload || {};
          state.aiAnswer =
            p.answer !== undefined
              ? p
              : { answer: p.result || "An error occurred. Please try again." };
        }
      })
      .addCase(fixCsvImport.fulfilled, (state, action) => {
        if (action.payload && action.payload.remaining_queries !== undefined) {
          state.aiCsvQueriesRemaining = action.payload.remaining_queries;
        }
      })
      .addCase(fixCsvImport.rejected, (state, action) => {
        if (action.payload && action.payload.quota_exceeded) {
          state.aiCsvQueriesRemaining = 0;
        }
      });
    addAsyncLifecycleCases(builder, {
      thunk: indexUserData,
      loadingKey: "aiIndexing",
    });
    builder
      .addCase(logout.fulfilled, () => initialState)
      .addCase(expiredLogout.fulfilled, () => initialState);
  },
});

// Surfaced for thunks.js — the streaming thunk dispatches these
// directly because SSE doesn't fit the pending/fulfilled/rejected
// shape that createAsyncThunk auto-wires.
export const {
  chatStreamStarted,
  chatChunkAppended,
  chatCompleted,
  chatStreamErrored,
} = aiSlice.actions;

export default aiSlice.reducer;
